import { useState } from 'react'
import type { Creator } from '../../constants/creators'
import { Image } from '../ui/Image'

/**
 * Renders a creator's avatar from `creator.image`, routed through Netlify
 * Image CDN for the right size + format. If the image fails to load (e.g.
 * asset not yet uploaded for a stubbed creator), falls back to a styled
 * circle showing the creator's initials so the layout doesn't collapse
 * into a browser broken-image icon.
 *
 * `size` is the rendered CSS pixel dimension; the underlying <Image>
 * generates a 1x/2x srcset so retina screens get the higher-density
 * variant from the CDN without overpaying on standard screens.
 */
export function CreatorAvatar({
  creator,
  size,
  className = '',
}: {
  creator: Creator
  size: number
  className?: string
}) {
  const [failed, setFailed] = useState(false)

  // Treat a missing src as "failed" up front: a plain <img src=""> renders a
  // browser broken-image icon and does NOT reliably fire onError (Chrome
  // resolves "" to the document URL), so the load-failure fallback never runs.
  if (failed || !creator.image) {
    const source = creator.fullName ?? creator.name
    const initials = source
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('')
    return (
      <div
        className={`flex items-center justify-center bg-cosmic-black/60 text-gold font-display tracking-wider ${className}`}
        aria-label={`${source} avatar`}
        role="img"
        style={{ width: size, height: size }}
      >
        {initials || '✦'}
      </div>
    )
  }

  return (
    <Image
      src={creator.image}
      width={size}
      height={size}
      alt={creator.fullName ?? creator.name}
      onError={() => setFailed(true)}
      loading="lazy"
      className={className}
    />
  )
}
